// The ways into an expedition: the button on a wreck, the banner that resumes one under way, and the host that loads
// the screen (and the Forge's engine with it) only once an expedition is opened.
import { useEffect, useState } from 'preact/hooks';
import type { ComponentType } from 'preact';
import { lang, t } from '../i18n/index.js';
import { useSig } from './useSig.js';
import { beginExpedition, expedition, expeditionOpen, expeditionRt, expeditionsOn, restUntil, rtPrep } from './expeditionStore.js';

/** « 14:05 »: when the team is back, in the player's clock. */
export const clock = (ms: number): string => new Date(ms).toLocaleTimeString(lang.value === 'fr' ? 'fr-CH' : 'en-GB', { hour: '2-digit', minute: '2-digit' });

/** A phone or a tablet-width window: expeditions are reserved to a computer (decision 0019). */
const isPhone = (): boolean => typeof window !== 'undefined' && (window.innerWidth < 900 || (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches && window.innerWidth < 1100));

/** On a wreck: explore it, or go back to the expedition already under way there. (Derelict stations are not forged
 *  yet — the Forge's siteOf takes wrecks only — so they get no button.) */
export function ExploreButton({ system, poi, kind }: { system: string; poi: string; kind: string }) {
  const on = useSig(expeditionsOn);
  const cur = useSig(expedition);
  const rest = useSig(restUntil);
  const rtOn = useSig(expeditionRt);
  const [busy, setBusy] = useState(false);
  if (!on || kind !== 'wreck') return null;
  // Decision 0019: expeditions are for a computer (browser on PC or Mac); a phone or a narrow window gets the message.
  if (isPhone()) return <button class="enter" disabled>{t('xpDesktopOnly')}</button>;
  if (cur && cur.system === system && cur.poi === poi && cur.outcome?.state === 'playing') return <button class="enter" onClick={() => { expeditionOpen.value = true; }}>{t('xpResume')}</button>;
  // The team rests after an expedition (decision 0015): the button says until when.
  if (rest !== null && rest > Date.now()) return <button class="enter" disabled>{t('xpResting').replace('{t}', clock(rest))}</button>;
  // Rules 7: the dungeon is prepared first (the team, the kit, the pods), the departure comes from that screen.
  if (rtOn) return <button class="enter" onClick={() => { rtPrep.value = { system, poi }; }}>{t('xpExplore')}</button>;
  return (
    <button class="enter" disabled={busy} onClick={() => { setBusy(true); void beginExpedition(system, poi).finally(() => setBusy(false)); }}>
      {t('xpExplore')}
    </button>
  );
}

/** An expedition still being played, screen closed: a line to resume it. */
export function ExpeditionBanner() {
  const cur = useSig(expedition);
  const open = useSig(expeditionOpen);
  if (!cur || open || cur.outcome?.state !== 'playing') return null;
  return (
    <div class="alerts">
      <button class="alert xp-resume" onClick={() => { expeditionOpen.value = true; }}>
        <i /> <span>{t('xpBanner').replace('{n}', cur.site.story.name)}</span> <b>{t('xpResume')} ›</b>
      </button>
    </div>
  );
}

export function ExpeditionHost() {
  const open = useSig(expeditionOpen);
  const exp = useSig(expedition);
  const prep = useSig(rtPrep);
  const [View, setView] = useState<ComponentType | null>(null);
  const [Rt, setRt] = useState<ComponentType | null>(null);
  const [Prep, setPrep] = useState<ComponentType | null>(null);
  // Rules 7 (the real-time dungeon) has its own screens, each its own chunk: the preparation, then the mission (which loads the
  // dungeon's engine and renderer only once it starts). The older rules keep the Forge's screen.
  const rt7 = exp?.rules === 7;
  useEffect(() => {
    if (open && !rt7 && !View) void import('./ExpeditionView.js').then((m) => setView(() => m.ExpeditionView));
    if (open && rt7 && !Rt) void import('./RtMission.js').then((m) => setRt(() => m.RtMission));
    if (prep && !Prep) void import('./RtPrep.js').then((m) => setPrep(() => m.RtPrep));
  }, [open, rt7, prep]);
  if (open && rt7 && Rt) return <Rt />;
  if (open && !rt7 && View) return <View />;
  return prep && Prep ? <Prep /> : null;
}
